import { useCallback, useEffect, useRef } from 'react';
import { useOptionalCallQueue } from '../contexts/CallQueueContext';
import { QueuedCall } from '../types/call-queue.types';

function formatPhone(phone: string): string {
  if (!phone) return '';
  if (phone.includes(' ') || phone.includes('-')) return phone;

  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('7')) {
    return `+7 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
  }
  return phone;
}

function formatElapsedTime(createdAt: number): string {
  const elapsed = Math.floor((Date.now() - createdAt) / 1000);
  const minutes = Math.floor(elapsed / 60);
  return `${minutes} мин.`;
}

export function useOverdueNotifications(): void {
  const callQueue = useOptionalCallQueue();

  const notifiedOverdueRef = useRef<Set<string>>(new Set());

  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch {
        // ignore
      }
    }
  }, []);

  const showNotification = useCallback((call: QueuedCall) => {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    const title = 'Звонок требует внимания!';
    const body = `${formatPhone(call.phone)} — в очереди ${formatElapsedTime(call.createdAt)}`;

    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: `call-queue-${call.callId}`,
      requireInteraction: true,
    });

    notification.onclick = () => {
      window.focus();
      if (callQueue) {
        callQueue.expandPanel();
        callQueue.enterCardMode(call.callId);
      }
      notification.close();
    };

    setTimeout(() => {
      try {
        notification.close();
      } catch {
        // ignore
      }
    }, 30000);
  }, [callQueue]);

  useEffect(() => {
    if (!callQueue) return;

    const { queue, settings } = callQueue;

    for (const call of queue) {
      if (
        call.priority === 'overdue' &&
        !notifiedOverdueRef.current.has(call.callId)
      ) {
        if (settings.notificationsEnabled) {
          notifiedOverdueRef.current.add(call.callId);
          requestNotificationPermission().then(() => {
            showNotification(call);
          });
        }
      }
    }

    const currentIds = new Set(queue.map((c) => c.callId));

    for (const id of notifiedOverdueRef.current) {
      if (!currentIds.has(id)) {
        notifiedOverdueRef.current.delete(id);
      }
    }
  }, [callQueue, requestNotificationPermission, showNotification]);
}

export default useOverdueNotifications;
